import * as React from 'react';
import { ReactComponent as ApiSproketIcon } from 'assets/code-sprocket-icon.svg';
import { ReactComponent as CaretIcon } from 'assets/dropdown-arrow.svg';
import { NavLink, useParams, useLocation, useHistory } from 'react-router-dom';
import { Loading } from './Loading';
import './ApiDetailsPage.css';
import { useGetAPIProductDetails, useGetPortalSummary } from '../api/hooks';
import { APIDetailsCard } from './APIDetailsCard';
import { ReactComponent as EnvironmentIcon } from 'assets/environment-icon.svg';
import { ReactComponent as VersionIcon } from 'assets/version-icon.svg';

export const versionParam = 'v';

export const ApiDetailsPage = () => {
  const { apiproductnamespace, apiproductname } = useParams<{ apiproductnamespace: string; apiproductname: string }>();

  const history = useHistory();
  const { apiProductDetails, apiProductDetailsError } = useGetAPIProductDetails({
    name: apiproductname!,
    namespace: apiproductnamespace!
  });

  let urlSearch = new URLSearchParams(useLocation().search);

  let initialVersionName = urlSearch.get(versionParam);
  // Fall back to an arbitrary environment + version if this page is visited without a version parameter
  if (!initialVersionName && !!apiProductDetails) {
    initialVersionName =
      apiProductDetails.environmentsList.find(env => !!env.versionsList.length)?.versionsList[0] ?? '';
  }

  const [currentEnvironment, setCurrentEnvironment] = React.useState(
    apiProductDetails?.environmentsList.find(env => !!env.versionsList.find(v => v === initialVersionName))
  );
  const [currentVersion, setCurrentVersion] = React.useState(
    currentEnvironment?.versionsList?.find(v => v === initialVersionName)
  );
  const [showDropdown, setShowDropdown] = React.useState(false);

  React.useEffect(() => {
    if (!currentEnvironment) {
      setCurrentEnvironment(
        apiProductDetails?.environmentsList.find(env => !!env.versionsList.find(v => v === initialVersionName)) ??
          apiProductDetails?.environmentsList[0]
      );
      setCurrentVersion(
        apiProductDetails?.environmentsList
          .find(env => !!env.versionsList.find(v => v === initialVersionName))
          ?.versionsList?.find(v => v === initialVersionName)
      );
    }
  }, [initialVersionName, apiproductname, apiproductnamespace, !!apiProductDetails]);

  const { portalSummary } = useGetPortalSummary();
  React.useEffect(() => {
    document.title = `${portalSummary?.displayName ?? 'Portal'} - ${apiproductname} ${currentVersion}`;
  }, [portalSummary?.displayName, currentVersion]);

  if (!apiProductDetails && !apiProductDetailsError) {
    return <Loading center={true} />;
  }

  if (!apiProductDetails) {
    return null;
  }

  return (
    <div className='api-details-container ' role='main'>
      <div className='api-details-headings'>
        <h1 className='flex items-center api-details-list-nav font-normal'>
          <ApiSproketIcon className='mr-3 fill-current text-blue-600gloo' title='' />
          <NavLink to={`/apis/`} exact>
            APIs
          </NavLink>
          <div className='api-details-caret-icon-holder'>
            <CaretIcon className='mr-3' title='' />
          </div>
          <span>{apiProductDetails?.displayname}</span>
        </h1>
        <div className='relative inline-block text-left'>
          <div>
            <button
              type='button'
              className='inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none hover:bg-gray-50 focus:border-blue-400gloo focus:ring-4 focus:ring-blue-500gloo focus:ring-opacity-10'
              id='api-details-options-menu'
              onClick={() => setShowDropdown(!showDropdown)}
              aria-haspopup='true'
              aria-expanded='true'>
              <EnvironmentIcon className='w-4 h-4 fill-current text-blue-600gloo ' title='' />

              <span className='mx-1 text-gray-700'>{currentEnvironment?.displayname} </span>
              <VersionIcon className='w-4 h-4 ml-1 fill-current text-blue-500gloo' title='' />
              <span className='mx-1 text-gray-700'>{currentVersion}</span>
              <svg
                className='w-5 h-5 ml-2 -mr-1 '
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
                fill='currentColor'
                aria-hidden='true'>
                <path
                  fillRule='evenodd'
                  d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </div>

          {showDropdown && (
            <div className='absolute right-0 w-56 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5'>
              <div
                className='py-1 divide-y '
                role='menu'
                aria-orientation='vertical'
                aria-labelledby='api-details-options-menu'>
                {apiProductDetails?.environmentsList
                  .sort((a, b) => (a.displayname === b.displayname ? 0 : a.displayname > b.displayname ? 1 : -1))
                  .map(env => (
                    <span className='' key={env.metadata?.uid}>
                      <div className='flex items-center px-4 py-3'>
                        <EnvironmentIcon className='w-4 h-4 mr-2 fill-current text-blue-600gloo' title='' />
                        <p className='text-sm font-medium text-gray-900 truncate'>{env.displayname}</p>
                      </div>

                      {env.versionsList.map(version => (
                        <li
                          role='button'
                          aria-label='Set version and environment'
                          key={version}
                          onClick={() => {
                            history.replace(`/apis/${apiproductnamespace}/${apiproductname}?v=${version}`);
                            setCurrentVersion(version);
                            setCurrentEnvironment(
                              apiProductDetails?.environmentsList?.find(e => e.metadata?.uid === env?.metadata?.uid)
                            );
                            setShowDropdown(false);
                          }}
                          className='block px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 hover:text-gray-900'>
                          <span>{version}</span>
                        </li>
                      ))}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <APIDetailsCard
        apiProduct={apiProductDetails}
        environmentRef={{
          name: currentEnvironment?.metadata?.name ?? '',
          namespace: currentEnvironment?.metadata?.namespace ?? ''
        }}
        version={currentVersion ?? ''}
      />
    </div>
  );
};
